<template>
	<div class="tw-bg-primary-700">
		<router-link
			@click="emit('navigated')"
			:to="{
				name: 'CustomersOverview',
			}"
			exact
			active-class="active-link">
			<div class="tw-text-primary-contrast tw-pl-6 tw-py-2 hover:tw-bg-primary-500">
				{{ t("multitenancy.module_label_overview") }}
			</div>
		</router-link>

		<div class="tw-text-primary-contrast tw-bg-primary-500">
			<div class="tw-flex tw-pl-3">
				<router-link
					@click="emit('navigated')"
					:to="{
						name: 'CustomersManagement',
					}"
					exact
					active-class="active-link">
					<div class="tw-text-primary-contrast tw-py-4 tw-ml-3 tw-font-semibold">
						{{ t("multitenancy.module_label_customers") }}
					</div>
				</router-link>
				<div class="tw-my-auto tw-ml-auto">
					<CollapseButton
						v-model="customersExpanded"
						class="tw-bg-transparent"
						@update:model-value="(v) => handleToggle('customers', !!v)"
						data-test="collapse-button-customers" />
				</div>
			</div>
			<TransitionCollapse>
        <div
          v-if="customersExpanded"
          class="tw-bg-primary-700 tw-pl-3">
          <div
            v-if="!customers.length"
            class="tw-text-sm tw-text-center tw-py-1"
            v-text="t('multitenancy.noCustomers')"
          />
          <router-link
						@click="emit('navigated')"
						v-for="customer in customers"
						:key="customer.id"
						:to="{
							name: 'CustomersLayout',
							params: {
								customerId: customer.id,
							},
						}">
						<div
							class="tw-flex hover:tw-bg-primary-500"
							:data-test="customer.name">
							<div class="left-spacer">
								<div class="line-segment" />
								<div class="tw-w-3 tw-h-3 tw-rounded-full tw-absolute tw-bg-primary-contrast"></div>
							</div>
							<div class="tw-my-2 tw-gap-2 tw-block tw-overflow-hidden tw-text-ellipsis">
								{{ customer.name }}
							</div>

							<div class="tw-ml-auto" />

							<div class="tw-my-auto tw-flex">
								<!-- TODO: Adjust the alarms according to the data modal and the endpoint -->
								<router-link
									v-if="
										alarms &&
										alarms.length &&
										alarms
											.filter((e) => e.alarming > 0)
											.map((e) => e.deviceid)
											.includes(Number(customer.id))
									"
									@click="emit('navigated')"
									:to="{
										name: 'CustomerAlarms',
										query: {
											customerId: customer.id,
										},
									}">
									<div>
										<div class="tw-text-status-red-500 tw-my-auto tw-mr-2 animate-alarm">
											<FontAwesomeIcon icon="fa-bell" />
										</div>
									</div>
								</router-link>

								<!-- TODO: Adjust the issues according to the data modal and the endpoint -->
								<router-link
									@click="emit('navigated')"
									v-if="getActiveIssues(Number(customer.id)).length > 0"
									:to="{
										name: 'CustomerOverview',
										query: {
											customerId: customer.id,
										},
									}">
									<div>
										<FontAwesomeIcon
											icon="fa-triangle-exclamation"
											class="tw-text-status-yellow-500 tw-my-auto tw-mr-2" />
									</div>
								</router-link>
							</div>
						</div>
					</router-link>
				</div>
			</TransitionCollapse>
		</div>
	</div>
</template>

<script setup lang="ts">
import {DXAlarm, DXIssue} from "@/types";
import {PropType, ref} from "vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import TransitionCollapse from "@/components/library/TransitionCollapse/TransitionCollapse.vue";
import CollapseButton from "@/components/library/CollapseButton/CollapseButton.vue";
import {useTranslation} from "i18next-vue";
import {createCollapseMenu, readCollapseMenu, toggleCollapseMenu} from "@/utils/userpreferences/collapseMenu";
import type {NavigationCustomer} from "./NavigationUtil";

const props = defineProps({
	customers: {
		type: Array as PropType<NavigationCustomer[]>,
		required: true,
	},
	alarms: {
		type: Object as PropType<DXAlarm[]>,
		required: true,
	},
	issues: {
		type: Object as PropType<{
			[key: number]: DXIssue[];
		}>,
		required: true,
	}
});

const {t} = useTranslation();

const initialCollapsedState = () => {
	const userPreference = readCollapseMenu("customers");
	if (userPreference === null) {
		createCollapseMenu("customers", true);
		return true;
	}
	return userPreference.toString() === "true";
};

const customersExpanded = ref(initialCollapsedState());

const handleToggle = (name: string, v: boolean) => {
	toggleCollapseMenu(name, v);
};
//TODO: Adjust the logic and the data modal later
const getActiveIssues = (customerID: number) => {
	const customerIssues = props.issues[customerID];
	if (!customerIssues) return [];
	return customerIssues.filter((issue) => issue.tsEnd === 0);
};

const emit = defineEmits(["navigated"]);
</script>

<style scoped lang="postcss">
a {
	@apply tw-text-inherit;
}

.left-spacer {
	@apply tw-w-6 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-relative;
}

.line-segment {
	@apply tw-h-full tw-w-0 tw-border-0 tw-border-l tw-border-solid tw-border-primary-contrast tw-ml-[-1px];
}

.active-link {
	@apply tw-text-secondary-500;
}
</style>
